import usePropertyService from 'api/services/propertyService'
import { clearCreateAdDraft } from 'api/utils/createAdDraftStorage'
import validatePassword from 'api/utils/passwordStrength'
import {
  buildPublishedPath,
  markListingOwnerIntent,
} from 'api/utils/pendingCreateAdFlow'
import { validateEmail } from 'api/utils/validateEmail'
import AuthPageShell from 'components/auth/AuthPageShell'
import { EmailInput } from 'components/common/field'
import PasswordInput from 'components/common/field/Password'
import PasswordStrengthHint from 'components/common/field/PasswordStrengthHint'
import PageLoading from 'components/common/loading/PageLoading'
import { useAuth } from 'pages/authenticated/AuthContext'
import { usePermissions } from 'pages/authenticated/PermissionsContext'
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  getFriendlyLoginErrorMessage,
  getFriendlyRegisterErrorMessage,
} from 'utils/loginErrorMessages'

export type CreateAdOwnerRegisterState = {
  publishPayload: Record<string, unknown>
  email?: string
  initialMode?: 'register' | 'login'
}

type PropertyResponse = {
  success: boolean
  message?: string
  data?: unknown
}

function parseListingId(data: unknown): string | null {
  if (data === null || data === undefined) return null
  if (typeof data === 'number' && Number.isFinite(data)) return String(data)
  if (typeof data === 'string' && data.trim() !== '') return data.trim()
  return null
}

const CreateAdOwnerRegisterPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { login, register, loginWithGoogle, isAuthenticated } = useAuth()
  const { refresh: refreshPermissions } = usePermissions()
  const { registerProperty } = usePropertyService()

  /** Blocks concurrent createProperty calls (effect vs form submit race). */
  const publishingRef = useRef(false)
  /**
   * Set synchronously before login/register awaits so the isAuthenticated
   * effect does not also publish after auth flips.
   */
  const authFromFormRef = useRef(false)

  const navState = location.state as CreateAdOwnerRegisterState | null
  const publishPayload = navState?.publishPayload
  const emailFromQuery = (searchParams.get('email') || '').trim().toLowerCase()
  const modeFromQuery = searchParams.get('mode') === 'login' ? 'login' : null

  const [mode, setMode] = useState<'register' | 'login'>(
    modeFromQuery ||
      (navState?.initialMode === 'login' ? 'login' : 'register')
  )
  const [email, setEmail] = useState(
    (emailFromQuery || navState?.email || '').trim().toLowerCase()
  )
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  // Already signed in with a payload → show publishing UI immediately (no form flash).
  const [loading, setLoading] = useState(
    () => Boolean(isAuthenticated && publishPayload)
  )
  const [publishError, setPublishError] = useState<string | null>(null)
  const [emailValid, setEmailValid] = useState(
    () =>
      validateEmail(
        (emailFromQuery || navState?.email || '').trim().toLowerCase()
      ) === true
  )

  const { mutateAsync: registerPropertyMutation } = useMutation(registerProperty)

  const canSubmit = useMemo(() => {
    if (!emailValid || !password) return false
    if (mode === 'register') {
      if (!validatePassword(password)) return false
      if (password !== confirmPassword) return false
      return true
    }
    return password.length >= 6
  }, [emailValid, password, confirmPassword, mode])

  const publishAndGoToResult = async () => {
    if (publishingRef.current) return
    publishingRef.current = true
    try {
      markListingOwnerIntent()
      await refreshPermissions({ intent: 'list_property' })

      const response = (await registerPropertyMutation(
        publishPayload as Record<string, unknown>
      )) as PropertyResponse

      if (!response?.success) {
        throw new Error(
          response?.message || String(t('createAd.errors.registerFailed'))
        )
      }

      const listingId = parseListingId(response.data)
      if (!listingId) {
        throw new Error(String(t('createAd.errors.registerFailed')))
      }

      clearCreateAdDraft()
      // Keep owner intent until email is verified so header stays Propriétaire.
      await refreshPermissions({ intent: 'list_property' })
      navigate(buildPublishedPath(listingId, 'created'), {
        replace: true,
        state: { listingId, mode: 'created' as const },
      })
    } catch (err) {
      publishingRef.current = false
      throw err
    }
  }

  // Landed already signed in (or auth restored) → publish once.
  // Skipped when the form/Google path owns the publish after login.
  useEffect(() => {
    if (!publishPayload || !isAuthenticated) return
    if (authFromFormRef.current || publishingRef.current) return

    setPublishError(null)
    setLoading(true)
    void publishAndGoToResult()
      .catch(err => {
        const message =
          err instanceof Error
            ? err.message
            : String(t('createAd.errors.registerFailed'))
        setPublishError(message)
        toast.error(message)
      })
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, publishPayload])

  if (!publishPayload || typeof publishPayload !== 'object') {
    return (
      <AuthPageShell
        title={t('createAd.ownerAuth.missingPayloadTitle')}
        subtitle={t('createAd.ownerAuth.missingPayloadBody')}
      >
        <Link
          to="/create-ad"
          state={{ newListing: true }}
          className="inline-flex min-h-[48px] w-full items-center justify-center rounded-xl bg-imovPrimary px-4 text-sm font-semibold text-white transition hover:bg-imovPrimaryDark"
        >
          {t('createAd.ownerAuth.backToForm')}
        </Link>
      </AuthPageShell>
    )
  }

  if (loading || (isAuthenticated && !publishError)) {
    return (
      <AuthPageShell
        title={t('createAd.ownerAuth.publishingTitle')}
        subtitle={t('createAd.ownerAuth.publishingBody')}
      >
        <PageLoading minHeightClassName="min-h-[8rem]" />
      </AuthPageShell>
    )
  }

  if (isAuthenticated && publishError) {
    return (
      <AuthPageShell
        title={t('createAd.ownerAuth.publishingTitle')}
        subtitle={publishError}
      >
        <button
          type="button"
          onClick={() => {
            setPublishError(null)
            setLoading(true)
            void publishAndGoToResult()
              .catch(err => {
                const message =
                  err instanceof Error
                    ? err.message
                    : String(t('createAd.errors.registerFailed'))
                setPublishError(message)
                toast.error(message)
              })
              .finally(() => setLoading(false))
          }}
          className="inline-flex min-h-[48px] w-full items-center justify-center rounded-xl bg-imovPrimary px-4 text-sm font-semibold text-white transition hover:bg-imovPrimaryDark"
        >
          {t('createAd.ownerAuth.retryPublish')}
        </button>
      <Link
        to="/create-ad"
        className="mt-3 block w-full py-2 text-center text-sm text-gray-500 transition hover:text-imovSecondary"
      >
        {t('createAd.ownerAuth.backToForm')}
      </Link>
      </AuthPageShell>
    )
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (loading || !canSubmit || publishingRef.current) return
    if (mode === 'register') {
      if (!validatePassword(password)) {
        toast.error(String(t('auth.passwordWeak')))
        return
      }
      if (password !== confirmPassword) {
        toast.error(String(t('auth.errorPasswordMismatch')))
        return
      }
    }

    // Claim publish before any await so the auth effect cannot double-create.
    authFromFormRef.current = true
    setPublishError(null)
    setLoading(true)
    try {
      markListingOwnerIntent()
      const normalizedEmail = email.trim().toLowerCase()
      try {
        if (mode === 'register') {
          await register(normalizedEmail, password, { asOwner: true })
        } else {
          await login(normalizedEmail, password, { asOwner: true })
        }
        await refreshPermissions({ intent: 'list_property' })
      } catch (err: unknown) {
        authFromFormRef.current = false
        toast.error(
          mode === 'register'
            ? getFriendlyRegisterErrorMessage(err)
            : getFriendlyLoginErrorMessage(err)
        )
        return
      }
      try {
        await publishAndGoToResult()
      } catch (err: unknown) {
        authFromFormRef.current = false
        const message =
          err instanceof Error
            ? err.message
            : String(t('createAd.errors.registerFailed'))
        setPublishError(message)
        toast.error(message)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleGoogle = async () => {
    if (loading || publishingRef.current) return
    authFromFormRef.current = true
    setPublishError(null)
    setLoading(true)
    try {
      markListingOwnerIntent()
      try {
        await loginWithGoogle({ allowRedirect: false, asOwner: true })
        await refreshPermissions({ intent: 'list_property' })
      } catch (err: unknown) {
        authFromFormRef.current = false
        toast.error(getFriendlyLoginErrorMessage(err))
        return
      }
      try {
        await publishAndGoToResult()
      } catch (err: unknown) {
        authFromFormRef.current = false
        const message =
          err instanceof Error
            ? err.message
            : String(t('createAd.errors.registerFailed'))
        setPublishError(message)
        toast.error(message)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthPageShell
      title={
        mode === 'register'
          ? t('createAd.ownerAuth.registerTitle')
          : t('createAd.ownerAuth.loginTitle')
      }
      subtitle={t('createAd.ownerAuth.pageSubtitle')}
    >
      <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-imovPrimary">
        {t('createAd.ownerAuth.badge')}
      </p>

      <form
        onSubmit={e => void handleSubmit(e)}
        className="flex flex-col gap-3 [&>div]:!my-0"
      >
        <EmailInput
          name="email"
          label={t('auth.emailAddress')}
          value={email}
          onChange={e =>
            setEmail(String(('value' in e ? e.value : '') || '').toLowerCase())
          }
          setIsValid={setEmailValid}
          isFirstInput
        />
        <PasswordInput
          name="password"
          label={t('auth.password')}
          value={password}
          onChange={e => setPassword(String(('value' in e ? e.value : '') || ''))}
          setIsValid={() => undefined}
          isValidationRequired={mode === 'register'}
        />
        {mode === 'register' ? (
          <>
            <PasswordStrengthHint password={password} />
            <PasswordInput
              name="confirmPassword"
              label={t('auth.confirmPassword')}
              value={confirmPassword}
              onChange={e =>
                setConfirmPassword(String(('value' in e ? e.value : '') || ''))
              }
              setIsValid={() => undefined}
              isValidationRequired={false}
            />
          </>
        ) : null}

        <button
          type="submit"
          disabled={!canSubmit || loading}
          className="mt-1 inline-flex min-h-[48px] items-center justify-center rounded-xl bg-imovPrimary px-4 text-sm font-semibold text-white transition hover:bg-imovPrimaryDark disabled:cursor-not-allowed disabled:bg-imovPrimary/40"
        >
          {loading ? (
            <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          ) : mode === 'register' ? (
            t('createAd.ownerAuth.registerCta')
          ) : (
            t('createAd.ownerAuth.loginCta')
          )}
        </button>
      </form>

      <div className="my-4 flex items-center gap-3">
        <span className="h-px flex-1 bg-gray-200" />
        <span className="text-xs text-gray-400">{t('auth.orContinueWith')}</span>
        <span className="h-px flex-1 bg-gray-200" />
      </div>

      <button
        type="button"
        disabled={loading}
        onClick={() => void handleGoogle()}
        className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 text-sm font-semibold text-imovSecondary transition hover:bg-gray-50 disabled:opacity-50"
      >
        {t('createAd.ownerAuth.googleCta')}
      </button>

      <p className="mt-5 text-center text-sm text-gray-600">
        {mode === 'register' ? (
          <>
            {t('createAd.ownerAuth.haveAccount')}{' '}
            <button
              type="button"
              className="font-semibold text-imovPrimary hover:text-imovPrimaryDark"
              onClick={() => setMode('login')}
            >
              {t('createAd.ownerAuth.switchToLogin')}
            </button>
          </>
        ) : (
          <>
            {t('createAd.ownerAuth.needAccount')}{' '}
            <button
              type="button"
              className="font-semibold text-imovPrimary hover:text-imovPrimaryDark"
              onClick={() => setMode('register')}
            >
              {t('createAd.ownerAuth.switchToRegister')}
            </button>
          </>
        )}
      </p>

      <Link
        to="/create-ad"
        className="mt-3 block w-full py-2 text-center text-sm text-gray-500 transition hover:text-imovSecondary"
      >
        {t('createAd.ownerAuth.backToForm')}
      </Link>
    </AuthPageShell>
  )
}

export default CreateAdOwnerRegisterPage
